import Modal from './ui/Modal';
import ConfirmDialog from './ui/ConfirmDialog';
import React, { useState } from 'react';
import { categories } from '../utils/categories';
import { VendorCombobox } from './VendorCombobox';
import { CustomDatePicker } from './CustomDatePicker';
import CustomDropdown from './CustomDropdown';
import { specificationsForStorage, specificationsToTextarea } from '../utils/specifications';
import { useEditorDraft, useEditorBack } from '../hooks/useEditorDraft';
import { ModPhotoField } from './vehicle-detail/ModPhotoField';

interface Modification {
  id: string;
  title: string;
  description: string;
  category: string;
  vendor: string;
  cost?: number;
  install_date?: string;
  notes?: string;
  part_number?: string;
  specifications?: any;
  parentComponent?: string;
  power_gain?: number;
  torque_gain?: number;
  weight_change?: number;
  image_url?: string | null;
}

interface Vendor { name: string; }

interface EditModificationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (modification: Modification, photoFile?: File | null) => Promise<void>;
  modification: Modification | null;
  existingModifications: Modification[];
  vendors: Vendor[];
  refreshVendors: () => Promise<void>;
  buildLabel?: string;
  draftKey?: string;
}

export const EditModificationModal: React.FC<EditModificationModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  modification,
  existingModifications,
  vendors,
  refreshVendors: _refreshVendors,
  buildLabel,
  draftKey
}) => {
  const initial = React.useMemo(() => ({
    title: modification?.title || '', category: modification?.category || '',
    description: modification?.description || '', cost: modification?.cost?.toString() || '',
    installDate: modification?.install_date || '', vendor: modification?.vendor || '',
    partNumber: modification?.part_number || '', notes: modification?.notes || '',
    parentComponent: modification?.parentComponent || '', specifications: specificationsToTextarea(modification?.specifications),
  }), [modification]);
  const [formData, setFormData, clearDraft] = useEditorDraft(draftKey, initial);
  const [confirmDiscard, setConfirmDiscard] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const savingRef = React.useRef(false);
  const formRef = React.useRef<HTMLFormElement>(null);
  const [photoFile, setPhotoFile] = useState<File | null>(null);
  const dirty = JSON.stringify(formData) !== JSON.stringify(initial) || Boolean(photoFile);
  const requestClose = () => {
    if (savingRef.current) return;
    if (dirty) setConfirmDiscard(true);
    else onClose();
  };
  useEditorBack(isOpen, requestClose, dirty || isSubmitting);

  const categoryNames = Array.from(new Set([...categories.map(category => category.name),
    ...existingModifications.map(part => part.category), formData.category].filter(Boolean)));
  if (!isOpen || !modification) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (savingRef.current) return;

    // Validation
    const newErrors: Record<string, string> = {};

    if (!formData.title.trim()) {
      newErrors.title = 'Enter a part name';
    }

    if (!formData.category) {
      newErrors.category = 'Category is required';
    }

    if (formData.cost && (!Number.isFinite(Number(formData.cost)) || Number(formData.cost) < 0)) {
      newErrors.cost = 'Enter a cost of zero or more';
    }

    setErrors(newErrors);
    if (Object.keys(newErrors).length) window.requestAnimationFrame(() => formRef.current?.querySelector<HTMLElement>('[aria-invalid="true"]')?.focus());

    if (Object.keys(newErrors).length === 0) {
      savingRef.current = true;
      setIsSubmitting(true);

      const specLines = specificationsForStorage(formData.specifications);

      const updatedModification: Modification = {
        ...modification,
        title: formData.title.trim(),
        category: formData.category,
        description: formData.description.trim() || '',
        cost: formData.parentComponent.trim() ? 0 : (formData.cost ? Number(formData.cost) : undefined),
        install_date: formData.installDate || undefined,
        vendor: formData.vendor || '',
        part_number: formData.partNumber.trim() || undefined,
        notes: formData.notes.trim() || undefined,
        parentComponent: formData.parentComponent.trim() || undefined,
        specifications: specLines.length > 0 ? specLines : undefined
      };

      try {
        await onSubmit(updatedModification, photoFile);
        clearDraft();
        setErrors({});
        onClose();
      } catch (error) {
        setErrors({ submit: error instanceof Error ? error.message : 'Could not save this modification. Please retry.' });
      } finally {
        savingRef.current = false;
        setIsSubmitting(false);
      }
    }
  };

  const updateField = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  return (
    <>
    <Modal isOpen={isOpen} title="Edit part" description={buildLabel} mobileFullscreen initialFocus="dialog" onClose={requestClose}
      size="lg" closeOnEscape={!isSubmitting} closeOnOverlayClick={!isSubmitting} className="part-editor p-4 sm:p-6"
      footer={<div className="space-y-2">
        <p role="status" className={`text-xs ${errors.submit ? 'text-red-300' : 'text-gray-400'}`}>
          {isSubmitting ? 'Saving…' : errors.submit ? 'Not saved. Your changes are kept here.' : dirty ? 'Unsaved changes' : 'No unsaved changes'}
        </p>
        <div className="flex gap-2">
          <button type="button" onClick={requestClose} disabled={isSubmitting} className="btn-secondary flex-1 justify-center min-h-[44px]">Cancel</button>
          <button type="submit" form="edit-part-form" disabled={isSubmitting} className="btn-primary flex-1 justify-center min-h-[44px]">{isSubmitting ? 'Saving…' : 'Save changes'}</button>
        </div>
      </div>}>
        <form id="edit-part-form" ref={formRef} onSubmit={handleSubmit} className="min-w-0 space-y-5" noValidate>
          <fieldset disabled={isSubmitting} className="min-w-0 space-y-5">
          {errors.submit && <p role="alert" className="input-error">{errors.submit}</p>}
          {/* Required Fields */}
          <div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label htmlFor="edit-part-name" className="input-label">
                  Part name *
                </label>
                <input
                  type="text" id="edit-part-name" required aria-invalid={!!errors.title}
                  value={formData.title}
                  onChange={(e) => updateField('title', e.target.value)}
                  className={`input ${errors.title ? 'border-red-500' : ''}`}
                  placeholder="e.g., Garrett GT2860RS Turbocharger"
                />
                {errors.title && <p className="input-error">{errors.title}</p>}
              </div>

              <div>
                <label htmlFor="edit-part-category" className="input-label">
                  Category *
                </label>
                <select id="edit-part-category" value={formData.category} onChange={e => updateField('category', e.target.value)}
                  className="input" required aria-invalid={!!errors.category}>
                  <option value="">Select a category</option>
                  {categoryNames.map(name => <option key={name} value={name}>{name}</option>)}
                </select>
                {errors.category && <p className="input-error">{errors.category}</p>}
              </div>
            </div>
          </div>

          {/* Optional Fields */}
          <details className="part-editor-details">
            <summary className="input-label min-h-[44px] flex items-center cursor-pointer">Optional details <span aria-hidden="true" className="ml-auto mr-2">+</span><span className="text-muted text-xs ml-2">Cost, brand, photo and more</span></summary>
            <div className="space-y-4">
              <div>
                <label htmlFor="edit-part-description" className="input-label">
                  Description
                </label>
                <textarea
                  id="edit-part-description" value={formData.description}
                  onChange={(e) => updateField('description', e.target.value)}
                  rows={3}
                  className="input"
                  placeholder="Brief description of the modification..."
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="edit-part-cost" className="input-label">
                    Cost ($)
                  </label>
                  <input
                    type="number" inputMode="decimal" aria-invalid={!!errors.cost}
                    step="0.01"
                    min="0"
                    id="edit-part-cost" value={formData.cost}
                    onChange={(e) => updateField('cost', e.target.value)}
                    className={`input ${errors.cost ? 'border-red-500' : ''}`}
                    placeholder="0.00"
                  />
                  {errors.cost && <p className="input-error">{errors.cost}</p>}
                </div>

                <div>
                  <CustomDatePicker
                    label="Install Date"
                    value={formData.installDate}
                    onChange={(date) => updateField('installDate', date)}
                    placeholder="Select install date..."
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="input-label">
                    Vendor
                  </label>
                  <VendorCombobox
                    id="part-vendor"
                    value={formData.vendor}
                    onChange={(value) => updateField('vendor', value)}
                    placeholder="Search or add vendor..."
                    vendors={vendors.map(v => v.name)}
                  />
                </div>

                <div>
                  <label htmlFor="edit-part-partNumber" className="input-label">
                    Part Number
                  </label>
                  <input
                    type="text"
                    id="edit-part-partNumber" value={formData.partNumber}
                    onChange={(e) => updateField('partNumber', e.target.value)}
                    className="input"
                    placeholder="e.g., GT2860RS-001"
                  />
                </div>
              </div>

              <div>
                <label className="input-label">
                  Parent Component
                </label>
                <CustomDropdown
                  value={formData.parentComponent}
                  onChange={(value) => updateField('parentComponent', value)}
                  options={[
                    { value: '', label: 'Select parent component (optional)' },
                    ...existingModifications.filter(mod => mod.id !== modification.id).map(mod => ({
                      value: mod.title,
                      label: mod.title
                    }))
                  ]}
                  placeholder="Select parent component (optional)"
                />
                <p className="input-helper">
                  Select if this modification is part of a larger component or system<br></br>
                  (If parent selected, will set cost to $0.00)</p>
              </div>

              <div>
                <label htmlFor="edit-part-specifications" className="input-label">
                  Specifications
                </label>
                <textarea
                  id="edit-part-specifications" value={formData.specifications}
                  onChange={(e) => updateField('specifications', e.target.value)}
                  rows={4}
                  className="input"
                  placeholder={'Final drive: 4.308\nRear differential: 3.309\nGear ratios: 3.000, 2.000, 1.471, 1.111, 0.857'}
                />
                <p className="input-helper">
                  Add one detail per line.
                </p>
              </div>

              <div>
                <ModPhotoField
                  existingUrl={modification.image_url}
                  value={photoFile}
                  onChange={setPhotoFile}
                  disabled={isSubmitting}
                />
              </div>

              <div>
                <label htmlFor="edit-part-notes" className="input-label">
                  Notes
                </label>
                <textarea
                  id="edit-part-notes" value={formData.notes}
                  onChange={(e) => updateField('notes', e.target.value)}
                  rows={3}
                  className="input"
                  placeholder="Additional notes or comments..."
                />
              </div>
            </div>
          </details>

          </fieldset>
        </form>
    </Modal>
    <ConfirmDialog isOpen={confirmDiscard} title="Discard unsaved changes?"
      description="Keep editing to preserve your changes."
      confirmLabel="Discard changes" cancelLabel="Keep editing" destructive
      onCancel={() => setConfirmDiscard(false)} onConfirm={() => { clearDraft(); onClose(); }} />
    </>
  );
};
